import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { createBooking } from '../../../../api/bookingApi';
import { getAvailableTables } from '../../../../api/tableAPI';
import { translateTableStatus } from '../../../../utils/stranslateUltils';
import { toISO, getLocalDateString } from '../../../../utils/dateUtils';
import '../css/Modal.css';

const AddBookingModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    customer: {
      customerName: '',
      phoneNumber: '',
      email: ''
    },
    numGuests: 1,
    tableIds: [],
    bookingAt:'',
    note: ''
  });

  // Separate state for first and last name inputs
  const [nameInputs, setNameInputs] = useState({
    firstName: '',
    lastName: ''
  });

  // Separate state for date and time inputs
  const [dateTimeInputs, setDateTimeInputs] = useState({
    date: null,
    time: ''
  });
  
  const [validationErrors, setValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Table selection states
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState([]);

  // Generate time options with 30-minute intervals
  const timeOptions = useMemo(() => {
    const times = [];
    for (let hour = 9; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(timeString);
      }
    }
    return times;
  }, []);

  // Fetch available tables
  const fetchTables = useCallback(async (date) => {
    try {
      setTablesLoading(true);
      const response = await getAvailableTables({ date: date });
      // Handle different response structures
      let tableData = [];
      if (response && response.data) {
        tableData = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        tableData = response;
      }
      
      setTables(tableData);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setValidationErrors(prev => [...prev, 'Failed to load available tables']);
    } finally {
      setTablesLoading(false);
    }
  }, []);

  // Fetch tables when both date and time are selected
  useEffect(() => {
    if (dateTimeInputs.date && dateTimeInputs.time) {
      const dateString = getLocalDateString(dateTimeInputs.date);
      fetchTables(dateString);
      // Reset selected tables when date/time changes
      setSelectedTableIds([]);
      setFormData(prev => ({
        ...prev,
        tableIds: []
      }));
    }
  }, [dateTimeInputs.date, dateTimeInputs.time, fetchTables]);

  const handleTableSelection = (tableId) => {
    const newSelectedTableIds = selectedTableIds.includes(tableId)
      ? selectedTableIds.filter(id => id !== tableId)
      : [...selectedTableIds, tableId];
    
    setSelectedTableIds(newSelectedTableIds);
    setFormData(prev => ({
      ...prev,
      tableIds: newSelectedTableIds
    }));

    // Clear validation errors when user selects tables
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleNameChange = (field, value) => {
    const newNameInputs = {
      ...nameInputs,
      [field]: value
    };
    setNameInputs(newNameInputs);

    // Update customerName in formData
    const fullName = `${newNameInputs.firstName} ${newNameInputs.lastName}`.trim();
    handleInputChange('customer.customerName', fullName);

    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleDateTimeChange = (field, value) => {
    const newDateTimeInputs = {
      ...dateTimeInputs,
      [field]: value,
    };
    setDateTimeInputs(newDateTimeInputs);

    const { date, time } = newDateTimeInputs;

    if (date && time) {
      const bookingAtValue = toISO(date, time);
      handleInputChange('bookingAt', bookingAtValue);
    }

    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const validateForm = () => {
    const errors = [];
    
    if (!nameInputs.firstName.trim()) {
      errors.push(t('admin.add_booking_modal.validation.first_name_required'));
    }
    
    if (!nameInputs.lastName.trim()) {
      errors.push(t('admin.add_booking_modal.validation.last_name_required'));
    }
    
    if (!formData.customer.phoneNumber.trim()) {
      errors.push(t('admin.add_booking_modal.validation.phone_required'));
    } else if (!/^\+?[\d\s\-()]+$/.test(formData.customer.phoneNumber)) {
      errors.push(t('admin.add_booking_modal.validation.phone_invalid'));
    }
    
    if (!formData.customer.email.trim()) {
      errors.push(t('admin.add_booking_modal.validation.email_required'));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer.email)) {
      errors.push(t('admin.add_booking_modal.validation.email_invalid'));
    }
    
    if (formData.numGuests < 1) {
      errors.push(t('admin.add_booking_modal.validation.guests_min'));
    }

    if (!dateTimeInputs.date) {
      errors.push(t('admin.add_booking_modal.validation.date_required'));
    }
    
    if (!dateTimeInputs.time) {
      errors.push(t('admin.add_booking_modal.validation.time_required'));
    }
 
    if (!formData.tableIds || formData.tableIds.length === 0) {
      errors.push(t('admin.add_booking_modal.validation.table_required'));
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await createBooking(formData);
      onSuccess(response);
      onClose();
    } catch (error) {
      console.error('Error creating booking:', error);
      setValidationErrors([t('admin.add_booking_modal.validation.booking_failed')]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderValidationErrors = () => {
    if (validationErrors.length === 0) return null;

    return (
      <div className="validation-errors">
        <h4>{t('admin.add_booking_modal.validation.title')}</h4>
        <ul>
          {validationErrors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>{t('admin.add_booking_modal.title')}</h2>
            <button className="close-btn" onClick={onClose} aria-label="Close modal">×</button>
          </div>
          
          <div className="modal-body">
            {renderValidationErrors()}
            
            <div className="edit-section">
              <h3>{t('admin.add_booking_modal.customer_info')}</h3>
              <div className="form-grid">
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.first_name')} *:</label>
                  <input
                    type="text"
                    value={nameInputs.firstName}
                    onChange={(e) => handleNameChange('firstName', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.add_booking_modal.placeholders.first_name')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.last_name')} *:</label>
                  <input
                    type="text"
                    value={nameInputs.lastName}
                    onChange={(e) => handleNameChange('lastName', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.add_booking_modal.placeholders.last_name')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.phone_number')} *:</label>
                  <input
                    type="text"
                    value={formData.customer.phoneNumber}
                    onChange={(e) => handleInputChange('customer.phoneNumber', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.add_booking_modal.placeholders.phone')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.email')} *:</label>
                  <input
                    type="email"
                    value={formData.customer.email}
                    onChange={(e) => handleInputChange('customer.email', e.target.value)}
                    className="form-input"
                    placeholder={t('admin.add_booking_modal.placeholders.email')}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="edit-section">
              <h3>{t('admin.add_booking_modal.booking_info')}</h3>
              <div className="form-grid">
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.num_guests')} *:</label>
                  <input
                    type="number"
                    value={formData.numGuests}
                    onChange={(e) => handleInputChange('numGuests', parseInt(e.target.value) || 1)}
                    className="form-input"
                    min="1"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.date')} *:</label>
                  <DatePicker
                    selected={dateTimeInputs.date}
                    onChange={(date) => handleDateTimeChange('date', date)}
                    minDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    className="form-input"
                    placeholderText={t('admin.add_booking_modal.placeholders.date')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-item">
                  <label>{t('admin.add_booking_modal.time')} *:</label>
                  <select
                    value={dateTimeInputs.time}
                    onChange={(e) => handleDateTimeChange('time',e.target.value)}
                    className="form-input"
                    disabled={isSubmitting}
                  >
                    <option value="">{t('admin.add_booking_modal.select_time')}</option>
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-item full-width">
                  <label>{t('admin.add_booking_modal.note')}:</label>
                  <textarea
                    value={formData.note}
                    onChange={(e) => handleInputChange('note', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder={t('admin.add_booking_modal.placeholders.note')}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* Available Tables Section */}
            <div className="edit-section">
              <h3>{t('admin.add_booking_modal.table_selection')}</h3>
              {dateTimeInputs.date && dateTimeInputs.time ? (
                <div className="table-selection-section">
                  {tablesLoading ? (
                    <div className="loading-message">{t('admin.add_booking_modal.table.loading')}</div>
                  ) : tables.length > 0 ? (
                    <div className="available-tables">
                      <label className="section-label">{t('admin.add_booking_modal.table.available')} *:</label>
                      <div className="table-options-grid">
                        {tables.map((table) => (
                          <div
                            key={table.tableId}
                            className={`table-option-card ${selectedTableIds.includes(table.tableId) ? 'selected' : ''}`}
                            onClick={() => handleTableSelection(table.tableId)}
                          >
                            <div className="table-card-header">
                              <span className="table-name">{table.tableName}</span>
                              <div className="table-checkbox">
                                <input
                                  type="checkbox"
                                  checked={selectedTableIds.includes(table.tableId)}
                                  onChange={() => handleTableSelection(table.tableId)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className="table-card-details">
                              <span className="table-detail">{t('admin.add_booking_modal.table.id')}: {table.tableId}</span>
                              <span className="table-detail">{t('admin.add_booking_modal.table.capacity')}: {table.capacity}</span>
                              <span className="table-detail">{t('admin.add_booking_modal.table.status')}: {translateTableStatus(t,table.status)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {selectedTableIds.length > 0 && (
                        <div className="selected-tables-info">
                          <span className="selected-count">
                            {t('admin.add_booking_modal.table.selected_count', { count: selectedTableIds.length })}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-tables-message">
                      {t('admin.add_booking_modal.table.none_available')}
                    </div>
                  )}
                </div>
              ) : (
                <div className="select-datetime-message">
                  {t('admin.add_booking_modal.table.select_datetime')}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('admin.add_booking_modal.buttons.cancel')}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSubmit}
              disabled={isSubmitting || formData.tableIds.length === 0}
            >
              {isSubmitting ? t('admin.add_booking_modal.buttons.creating') : t('admin.add_booking_modal.buttons.create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBookingModal;